import React, { useEffect, useState } from "react";
import { View, TouchableOpacity } from "react-native";

import Text from "components/Text";
import Icon from "components/Icon";
import Input from "components/Input";
import Empty from "components/Empty";
import Error from "components/Error";
import Loading from "components/Loading";
import Separator from "components/Separator";
import ModalInfinityList from "components/ModalInfinityList";
import AnimatedInfinityList from "components/AnimatedInfinityList";

import { styles } from "./styles";
import { sizes, colors, strings } from "assets/constants";

import { useFlag } from "hooks";
import { liveSearchInput } from "utils";

import { useCities } from "clients/apis";

let timer: ReturnType<typeof setTimeout>;

export default function CityPicker(props: PickerT) {
  const { id, value, onSelect, error, hideError, onPress } = props;
  const val = value as LocationT;

  const [name, setName] = useState("");
  const {
    isError,
    error: citiesError,
    isRefetching,
    refetch,
    isLoading: citiesFetching,
    data: cities,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCities({ name, id });

  const { flg: modal, on: show, off: hide } = useFlag();
  const { flg: typing, on: startTyping, off: stopTyping } = useFlag();

  const _onHide = () => {
    setName("");
    hide();
  };

  const [valueState, setValueState] = useState<LocationT | undefined>();

  useEffect(() => {
    setValueState(val);
  }, [val]);

  useEffect(() => {
    id && refetch();
  }, [id, refetch]);

  const _renderItem = (items: []) => {
    return items.map((city: LocationT, key: number) => (
      <React.Fragment key={String(key)}>
        <TouchableOpacity
          style={styles.item}
          onPress={() => {
            onSelect && onSelect(city);
            setName("");
            setValueState(city);
            hide();
          }}
        >
          <Text>{city.text}</Text>
        </TouchableOpacity>
        <Separator size={"80%"} />
      </React.Fragment>
    ));
  };

  const _renderList = () => {
    if (citiesFetching || typing) {
      return <Loading style={{ marginTop: 20 }} />;
    }

    if (isError) {
      return (
        <Error error={citiesError} reset={refetch} loading={isRefetching} />
      );
    }

    if (cities?.pages?.[0]?.data?.data?.length === 0) {
      return <Empty message={strings.no_city} />;
    }

    return (
      <AnimatedInfinityList
        onRefresh={refetch}
        responseData={cities}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        renderItemList={_renderItem}
      />
    );
  };

  return (
    <>
      <>
        <TouchableOpacity
          onPress={onPress ?? show}
          activeOpacity={sizes.tapOpacity}
          style={{ ...styles.inputContainer }}
        >
          <View style={styles.iconContainer}>
            <Icon name="chevron-down" />
          </View>
          <View style={styles.textContainer}>
            <Text
              color={
                valueState?.text ? colors.textColorDefault : colors.placeholder
              }
            >
              {valueState?.text || strings.pick_city}
            </Text>
          </View>

          <View style={styles.iconContainer}>
            <Icon name="map-pin" />
          </View>
        </TouchableOpacity>

        {!hideError && (
          <View style={styles.errorContainer}>
            <Text size={"xs"} color={colors.danger}>
              {error}
            </Text>
          </View>
        )}
      </>

      <ModalInfinityList close={_onHide} visible={modal}>
        <View style={styles.searchInputContainer}>
          <Input
            style={styles.searchInput}
            hideError
            icon={{ name: "search" }}
            placeholder={strings.search}
            onChangeText={(inputName) => {
              liveSearchInput(
                inputName,
                name,
                setName,
                timer,
                startTyping,
                stopTyping
              );
              refetch();
            }}
          />
        </View>
        {_renderList()}
      </ModalInfinityList>
    </>
  );
}
